// src/components/institution/posts/PostCard.jsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  Calendar, 
  Globe, 
  Lock, 
  User, 
  Heart, 
  MessageCircle, 
  Share2, 
  MoreHorizontal, 
  Edit2, 
  Trash2,
  Play,
  Download,
  ExternalLink,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Volume2,
  VolumeX
} from 'lucide-react';

const POST_STATUS_CONFIG = {
  pending: {
    label: 'Pending Review',
    icon: Clock,
    color: 'text-yellow-600 bg-yellow-100'
  },
  approved: {
    label: 'Published',
    icon: CheckCircle,
    color: 'text-green-600 bg-green-100'
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    color: 'text-red-600 bg-red-100'
  }
};

export const PostCard = ({ 
  post, 
  user, 
  isModerator = false, 
  onEdit, 
  onDelete, 
  onModerate,
  onLike,
  onComment,
  onShare 
}) => {
  const [showActions, setShowActions] = useState(false);
  const [expandedMedia, setExpandedMedia] = useState(null);
  const [videoMuted, setVideoMuted] = useState({});
  const [externalVideoLoaded, setExternalVideoLoaded] = useState({});
  const videoRefs = useRef({});
  const iframeRefs = useRef({});

  const canEdit = user && (user.uid === post.authorId || isModerator);
  const statusConfig = POST_STATUS_CONFIG[post.status] || POST_STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  const formatDate = (date) => {
    const postDate = date?.toDate ? date.toDate() : new Date(date);
    return postDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTags = (tags) => {
    if (!tags) return [];
    if (Array.isArray(tags)) return tags;
    if (typeof tags === 'string') return tags.split(',').map(t => t.trim()).filter(t => t);
    return [];
  };

  // ✅ FIXED: Helper to get media type handling both 'type' and 'fileType'
  const getMediaType = (item) => {
    // Check type field first
    if (item.type) {
      if (item.type === 'external-video') return 'external-video';
      if (item.type === 'image' || item.type.startsWith?.('image/')) return 'image';
      if (item.type === 'video' || item.type.startsWith?.('video/')) return 'video';
      if (item.type === 'document') return 'document';
    }
    
    // Check fileType field
    if (item.fileType) {
      if (item.fileType.startsWith('image/')) return 'image';
      if (item.fileType.startsWith('video/')) return 'video';
      return 'document';
    }
    
    return 'unknown';
  };

  // ✅ FIXED: Separate media by type using the helper
  const media = post.media || [];
  const images = media.filter(item => getMediaType(item) === 'image');
  const uploadedVideos = media.filter(item => getMediaType(item) === 'video');
  const externalVideos = media.filter(item => getMediaType(item) === 'external-video');
  const documents = media.filter(item => getMediaType(item) === 'document');

  const postTags = getTags(post.tags);

  // Toggle mute for uploaded videos
  const toggleMute = (videoId) => {
    setVideoMuted(prev => ({ ...prev, [videoId]: !prev[videoId] }));
    if (videoRefs.current[videoId]) {
      videoRefs.current[videoId].muted = !videoMuted[videoId];
    }
  };

  // ✅ FIXED: Setup intersection observer for autoplay (uploaded videos)
  useEffect(() => {
    const observers = [];
    
    Object.keys(videoRefs.current).forEach(videoId => {
      const video = videoRefs.current[videoId];
      if (video) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                video.play().catch(err => console.log('Autoplay prevented:', err));
              } else {
                video.pause();
              }
            });
          },
          { threshold: 0.5 }
        );
        
        observer.observe(video);
        observers.push(observer);
      }
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [uploadedVideos]);

  // ✅ FIXED: Setup intersection observer for external videos (YouTube, etc.)
  useEffect(() => {
    const observers = [];
    
    Object.keys(iframeRefs.current).forEach(iframeId => {
      const iframe = iframeRefs.current[iframeId];
      if (iframe) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting && !externalVideoLoaded[iframeId]) {
                // Load iframe with autoplay
                const video = externalVideos[parseInt(iframeId.replace('iframe-', ''))];
                if (video && video.url) {
                  // Add autoplay and mute parameters for YouTube
                  let embedUrl = video.url;
                  if (video.platform === 'youtube') {
                    embedUrl += (embedUrl.includes('?') ? '&' : '?') + 'autoplay=1&mute=1&playsinline=1';
                  } else if (video.platform === 'vimeo') {
                    embedUrl += (embedUrl.includes('?') ? '&' : '?') + 'autoplay=1&muted=1';
                  } else if (video.platform === 'loom') {
                    embedUrl += (embedUrl.includes('?') ? '&' : '?') + 'autoplay=1';
                  }
                  iframe.src = embedUrl;
                  setExternalVideoLoaded(prev => ({ ...prev, [iframeId]: true }));
                }
              }
            });
          },
          { threshold: 0.3 }
        );
        
        observer.observe(iframe);
        observers.push(observer);
      }
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [externalVideos, externalVideoLoaded]);

  const renderMediaItem = (mediaItem, index) => {
    const mediaType = getMediaType(mediaItem);
    const { url, fileName } = mediaItem;
    
    switch (mediaType) {
      case 'image':
        return (
          <div 
            key={index}
            className="cursor-pointer hover:opacity-90 transition-opacity group relative"
            onClick={() => setExpandedMedia(mediaItem)}
          >
            <img 
              src={url} 
              alt={fileName || mediaItem.originalFileName || `Image ${index + 1}`} 
              className="w-full h-auto object-cover" 
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all" />
          </div>
        );
      
      case 'video':
        return (
          <div key={index} className="relative group">
            <video 
              ref={el => videoRefs.current[`video-${index}`] = el}
              src={url}
              muted={videoMuted[`video-${index}`] !== false}
              loop
              playsInline
              className="w-full h-48 object-cover rounded-lg"
              preload="metadata"
            />
            {/* Mute/Unmute Button */}
            <button
              onClick={() => toggleMute(`video-${index}`)}
              className="absolute bottom-3 right-3 p-2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white rounded-full transition-all opacity-0 group-hover:opacity-100"
            >
              {videoMuted[`video-${index}`] !== false ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </button>
          </div>
        );
      
      case 'document':
        return (
          <a
            key={index}
            href={url}
            download={fileName || mediaItem.originalFileName}
            className="flex items-center gap-3 p-4 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Download className="h-5 w-5 text-gray-600" />
            <span className="text-sm text-gray-900">{fileName || mediaItem.originalFileName || 'Document'}</span>
          </a>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-shadow overflow-hidden mb-0">
      {/* Post Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            {/* Author Avatar */}
            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
              {post.authorPhoto ? (
                <img src={post.authorPhoto} alt={post.authorName} className="w-full h-full object-cover" />
              ) : (
                <User className="h-5 w-5 text-gray-500" />
              )}
            </div>
            
            {/* Author Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">{post.authorName}</h3>
                
                {/* Visibility Badge */}
                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  post.visibility === 'public' 
                    ? 'bg-green-100 text-green-700'
                    : post.visibility === 'internal'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {post.visibility === 'public' ? (
                    <><Globe className="h-3 w-3" /> Public</>
                  ) : post.visibility === 'internal' ? (
                    <><Lock className="h-3 w-3" /> Internal</>
                  ) : (
                    <><User className="h-3 w-3" /> Private</>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                <Calendar className="h-3 w-3" />
                <span>{formatDate(post.createdAt)}</span>
              </div>
            </div>

            {/* Actions Menu */}
            {canEdit && (
              <div className="relative">
                <button
                  onClick={() => setShowActions(!showActions)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <MoreHorizontal className="h-5 w-5 text-gray-500" />
                </button>

                {showActions && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                    <button
                      onClick={() => {
                        onEdit?.();
                        setShowActions(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Edit2 className="h-4 w-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        onDelete?.();
                        setShowActions(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Status Badge */}
        {post.status && post.status !== 'approved' && (
          <div className="mt-3">
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
              <StatusIcon className="h-3 w-3" />
              {statusConfig.label}
            </span>
          </div>
        )}
      </div>

      {/* Post Content */}
      <div className="px-6 pb-4">
        {post.title && (
          <h2 className="text-xl font-bold text-gray-900 mb-3">{post.title}</h2>
        )}
        <div className="prose prose-sm max-w-none text-gray-700">
          {post.content?.split('\n').map((paragraph, index) => (
            paragraph.trim() && <p key={index} className="mb-2">{paragraph}</p>
          ))}
        </div>
      </div>

      {/* Images Grid */}
      {images.length > 0 && (
        <div className={`px-0 pb-4 ${
          images.length === 1 ? '' : 
          images.length === 2 ? 'grid grid-cols-2 gap-3' : 
          'grid grid-cols-2 md:grid-cols-3 gap-3'
        }`}>
          {images.map(renderMediaItem)}
        </div>
      )}

      {/* ✅ FIXED: External Videos with Lazy Load and Autoplay */}
      {externalVideos.length > 0 && (
        <div className="px-0 pb-4 space-y-4">
          {externalVideos.map((video, index) => (
            <div 
              key={index} 
              className="relative w-full overflow-hidden bg-black" 
              style={{ paddingBottom: '56.25%' }}
            >
              <iframe
                ref={el => iframeRefs.current[`iframe-${index}`] = el}
                title={`${video.platform} video ${index + 1}`}
                className="absolute top-0 left-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
              {/* Loading Placeholder */}
              {!externalVideoLoaded[`iframe-${index}`] && (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-gray-900">
                  <Play className="h-16 w-16 text-white opacity-70" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Uploaded Videos with Muted Autoplay */}
      {uploadedVideos.length > 0 && (
        <div className={`px-0 pb-4 ${
          uploadedVideos.length === 1 ? '' : 'grid grid-cols-2 gap-3'
        }`}>
          {uploadedVideos.map(renderMediaItem)}
        </div>
      )}

      {/* Documents */}
      {documents.length > 0 && (
        <div className="px-0 pb-4">
          <div className="space-y-2">
            {documents.map(renderMediaItem)}
          </div>
        </div>
      )}

      {/* Tags */}
      {postTags.length > 0 && (
        <div className="px-6 pb-4">
          <div className="flex flex-wrap gap-2">
            {postTags.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Moderation Actions */}
      {isModerator && post.status === 'pending' && (
        <div className="px-6 pb-4">
          <div className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <span className="text-sm text-yellow-800 font-medium flex-1">
              This post requires moderation
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => onModerate?.(post.id, 'approved')}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-1"
              >
                <CheckCircle className="h-4 w-4" />
                Approve
              </button>
              <button
                onClick={() => onModerate?.(post.id, 'rejected')}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 flex items-center gap-1"
              >
                <XCircle className="h-4 w-4" />
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Engagement Actions */}
      <div className="border-t border-gray-100 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => onLike?.(post.id)}
              className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
            >
              <Heart className="h-5 w-5" />
              <span className="text-sm font-medium">{post.likes || 0}</span>
            </button>
            <button
              onClick={() => onComment?.(post.id)}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
            >
              <MessageCircle className="h-5 w-5" />
              <span className="text-sm font-medium">{post.comments || 0}</span>
            </button>
          </div>
          <button
            onClick={() => onShare?.(post.id)}
            className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors"
          >
            <Share2 className="h-5 w-5" />
            <span className="text-sm font-medium">Share</span>
          </button>
        </div>
      </div>

      {/* Media Lightbox */}
      {expandedMedia && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setExpandedMedia(null)}
        >
          <button
            onClick={() => setExpandedMedia(null)}
            className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100"
          >
            <XCircle className="h-6 w-6 text-gray-900" />
          </button>
          <img
            src={expandedMedia.url}
            alt={expandedMedia.fileName}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};